import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../components/Header";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import Session from "../utils/sessionStorage";
import { CiBarcode } from "react-icons/ci";
import { FiRefreshCw } from "react-icons/fi";
import ScannerModal from "../components/ScannerModal";
import ErrorModal from "../components/ErrorModal";
import PrivacyConsent from "../components/privacy/PrivacyConsent";
import { countries } from "../constants";
import Input from "../components/form/Input";
import { dealerSdkPayload, isValidDealerSdkResponse, mapDealerSdkFields, readSdkData } from "../utils/dealerSdk";

const MIN_AGE = 18;

// True only when a valid DOB (YYYY-MM-DD) shows the person is younger than MIN_AGE (even by one day).
const isUnderAge = (dob) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob || "")) return false;
    const today = new Date();
    const cutoff = new Date(today.getFullYear() - MIN_AGE, today.getMonth(), today.getDate());
    const pad = (n) => String(n).padStart(2, "0");
    const cutoffStr = `${cutoff.getFullYear()}-${pad(cutoff.getMonth() + 1)}-${pad(cutoff.getDate())}`;
    return dob > cutoffStr;
};

// TEMP (local testing): hardcoded SDK data; route-state, validity and age checks bypassed. Revert before commit.
const MOCK_SDK_RESPONSE = {
    success: true,
    refNo: "a9ec57a6eb4e7056440fa407587d257e",
    bvrId: "0c51ca3d-6008-4d26-96f9-8382f02ac32a",
    bvrData: {
        type: "passport",
        data: {
            expiryDate: "17/04/2035",
            country: null,
            city: null,
            lastName: "ARIF",
            firstName: "TAYYAB",
            documentType: "PASSPORT",
            gender: "MALE",
            religion: null,
            address: null,
            birthDate: "30/07/1997",
            issuingCountry: null,
            nationality: "PAK",
            postcode: null,
            state: null,
            idNumber: "AV7135032",
            userKey: "2a3f8417825b21a5f6cebb418200ad4a79c90be63625873939c428f8df3cb5e2",
            countryId: null,
            nationalityId: null,
            issuingCountryId: null,
            livenessUrl: ["399FFE51-0E18-423F-9A69-25B32C041CEC"],
            documentBackUrl: null,
            documentFrontUrl: "D302C607-1144-4282-A2C6-7F63DD503D5F",
            identityImageBase64: null,
            faceImageBase64: null,
        },
    },
    sdkVersion: "0.2.5",
};

const DealerRegister = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const sdkResponse = location.state?.sdkResponse; // For local testing, use mock data if no state is provided.
    if (!isValidDealerSdkResponse(sdkResponse)) return <Navigate to="/home" replace />;
    // Check age from the SDK response before rendering the form.
    if (isUnderAge(mapDealerSdkFields(sdkResponse).date_of_birth)) {
        return (
            <>
                <Header />
                <ErrorModal
                    title="Age Restriction"
                    message="Customer is below 18 years old. Please contact the support team to register a SIM for a minor."
                    close={() => navigate("/home", { replace: true })}
                />
            </>
        );
    }
    return <DealerRegisterForm key={location.key} sdkResponse={sdkResponse} />;
};

const DealerRegisterForm = ({ sdkResponse }) => {
    const doctype = readSdkData(sdkResponse).type.toUpperCase();
    const isPassport = doctype === "PASSPORT";
    const [states, setStates] = useState([]);
    const [availableNos, setAvailableNos] = useState([]);
    const [sameAddress, setSameAddress] = useState(false);
    const [loading, setLoading] = useState(false);
    const [numbersLoading, setNumbersLoading] = useState(false);
    const [isMsisdnDropdownOpen, setIsMsisdnDropdownOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [useV1Api, setUseV1Api] = useState(false);
    const [errorModal, setErrorModal] = useState({ open: false, message: "" });
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const token = Session.get("token");
    const navigate = useNavigate();
    const submitting = useRef(false);
    const sdkFields = useMemo(() => mapDealerSdkFields(sdkResponse, states), [sdkResponse, states]);
    const isLocked = (name) => name !== "first_name" && Object.prototype.hasOwnProperty.call(sdkFields, name);

    const [draftForm, setForm] = useState({
        full_name: "",
        first_name: sdkFields.first_name || "",
        gender: "",
        email: "",
        date_of_birth: "",
        identity_expiry_date: "",
        identity_type: doctype || "",
        identity_no: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postal_code: "",
        msisdn: "",
        iccid: "",
        // Additional fields for passport
        foreigner_type: "",
        alt_mobile_no: "",
        issuance_country: "",
        stay_address_1: "",
        stay_address_2: "",
        stay_city: "",
        stay_postal_code: "",
        stay_state: "",
        ugu_ref_code: "",
        // Student fields
        institute_name: "",
        institute_address_1: "",
        institute_address_2: "",
        institute_city: "",
        institute_postal_code: "",
        institute_state: "",
        institute_ref_no: "",
        // Worker fields
        employer_name: "",
        employer_address_1: "",
        employer_address_2: "",
        employer_city: "",
        employer_postal_code: "",
        employer_state: "",
        emp_ref_no: "",
        // Tourist field
        hotel_name: ""
    });
    // First name is prefilled once but remains editable; other SDK fields stay locked.
    const form = { ...draftForm, ...sdkFields, first_name: draftForm.first_name };

    // Additional identity and approval details.
    const isMykad = doctype === "MYKAD";
    const showApprovalFields = isMykad || isPassport;
    const [approvalForm, setApprovalForm] = useState({
        current_address: "",
        identity_doc_type: "", // optional
        // Child fields of doc info type
        identity_name_info: "",
        identity_address_info: "",
        identity_phone_info: "",
        identity_info_no: "",
        identity_relationship_info: "",
    });
    const docInfoTypeOptions = isPassport
        ? [
            // { value: "supervisor", label: "Supervisor" },
            { value: "work_permit", label: "Work Permit" },
            { value: "student", label: "Student" },
            { value: "tourist", label: "Tourism" },
            // { value: "refugee", label: "Refugee" },
        ]
        : [{ value: "supervisor", label: "Supervisor" }];

    const isSupervisorDoc = approvalForm.identity_doc_type === "supervisor";
    // Address is required for work permit, student, tourist and refugee.
    const requiresAddressInfo = ["work_permit", "student", "tourist", "refugee"].includes(approvalForm.identity_doc_type);

    // Foreigner type is derived from the document info type (passport only).
    const DOC_TYPE_TO_FOREIGNER_TYPE = {
        work_permit: "Worker",
        student: "Student",
        tourist: "Tourist",
    };

    const handleApprovalChange = (e) => {
        const { name, value } = e.target;
        if (name === "identity_doc_type" && isPassport) {
            setForm(prev => ({
                ...prev,
                foreigner_type: DOC_TYPE_TO_FOREIGNER_TYPE[value] || "",
            }));
        }
        setApprovalForm(prev => ({
            ...prev,
            [name]: value,
            // Child fields are cleared when the document info type is unselected.
            ...(name === "identity_doc_type" && !value ? {
                identity_name_info: "",
                identity_address_info: "",
                identity_phone_info: "",
                identity_info_no: "",
                identity_relationship_info: "",
            } : {}),
        }));
    };

    const getAvailableNos = useCallback(async (clearSelectedNumber = false) => {
        setNumbersLoading(true);
        try {
            const response = await axiosInstance.get("api/v1/available-numbers", {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                setAvailableNos(response.data.available_numbers);

                if (clearSelectedNumber) {
                    setForm(prev => ({
                        ...prev,
                        msisdn: ""
                    }));
                    const currentFormData = JSON.parse(sessionStorage.getItem('dealerRegisterForm') || '{}');
                    sessionStorage.setItem('dealerRegisterForm', JSON.stringify({
                        ...currentFormData,
                        msisdn: ""
                    }));
                }
            }
        } catch (error) {
            setErrorModal({ open: true, message: error?.response?.data?.message || "Failed to load available numbers" });
        } finally {
            setNumbersLoading(false);
        }
    }, [token]);

    // Fetch states and available numbers on component mount
    useEffect(() => {
        async function getStates() {
            try {
                const response = await axiosInstance.get("api/v1/get-states", {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.status === 200) {
                    setStates(response.data.states);
                }
            } catch (error) {
                setErrorModal({ open: true, message: error?.response?.data?.message || "Failed to load states" });
            }
        }

        getStates();
        getAvailableNos();
    }, [token, getAvailableNos]);

    useEffect(() => {
        if (sameAddress && isPassport) {
            setForm((prev) => ({
                ...prev,
                address1: prev.stay_address_1,
                address2: prev.stay_address_2,
                city: prev.stay_city,
                state: prev.stay_state,
                postal_code: prev.stay_postal_code,
            }));
        }
    }, [
        sameAddress,
        isPassport,
        form.stay_address_1,
        form.stay_address_2,
        form.stay_city,
        form.stay_state,
        form.stay_postal_code,
    ]);

    useEffect(() => {
        const savedFormData = sessionStorage.getItem('dealerRegisterForm');
        if (savedFormData) {
            const parsedData = JSON.parse(savedFormData);
            setForm(prev => ({
                ...prev,
                iccid: parsedData.iccid || "",
                msisdn: parsedData.msisdn || ""
            }));
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (isLocked(name)) return;
        setForm(prev => ({
            ...prev,
            [name]: value
        }));
        if (name === 'msisdn') {
            setIsMsisdnDropdownOpen(true);
        }

        // Save specific fields to session storage
        if (name === 'full_name' || name === 'first_name' || name === 'iccid' || name === 'msisdn') {
            const currentFormData = JSON.parse(sessionStorage.getItem('dealerRegisterForm') || '{}');
            const updatedFormData = {
                ...currentFormData,
                [name]: value
            };
            sessionStorage.setItem('dealerRegisterForm', JSON.stringify(updatedFormData));
        }
    };

    const handleMsisdnSelect = (msisdn) => {
        setForm(prev => ({
            ...prev,
            msisdn
        }));
        const currentFormData = JSON.parse(sessionStorage.getItem('dealerRegisterForm') || '{}');
        sessionStorage.setItem('dealerRegisterForm', JSON.stringify({
            ...currentFormData,
            msisdn
        }));
        setIsMsisdnDropdownOpen(false);
    };

    const validateForm = () => {
        if (!form.full_name) return "Full Name is required";
        if (!form.first_name?.trim()) return "First Name is required";
        if (!form.gender) return "Gender is required";

        if (!form.email) {
            return "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            return "Please enter a valid email address";
        }

        if (!form.date_of_birth) return "Date of Birth is required";
        if (!form.identity_type) return "Identity Type is required";
        if (!form.identity_no) return "Identity Number is required";
        if (!form.address1) return "Address Line 1 is required";
        if (!form.city) return "City is required";
        if (!form.state) return "State is required";
        if (!form.postal_code) return "Postcode is required";

        if (!form.iccid) {
            return "ICCID is required";
        } else if (!/^\d+$/.test(form.iccid)) {
            return "ICCID should contain only digits";
        }

        if (!form.msisdn) {
            return "Mobile Number is required";
        } else if (!/^\d+$/.test(form.msisdn)) {
            return "Mobile Number should contain only digits";
        } else if (!availableNos.some(number => String(number.MSISDN) === String(form.msisdn))) {
            return "Please select a mobile number from the loaded list";
        }

        // Additional validation for passport
        if (isPassport) {
            if (!form.foreigner_type) return "Document Info Type is required";
            if (!form.issuance_country) return "Issuance country is required";
            if (!form.stay_address_1) return "Staying address is required";
            if (!form.stay_city) return "Staying city is required";
            if (!form.stay_state) return "Staying state is required";
            if (!form.stay_postal_code) return "Staying postcode is required";

            if (form.alt_mobile_no && !/^\d+$/.test(form.alt_mobile_no)) {
                return "Alternate mobile should contain only digits";
            }

            // Validation based on foreigner type
            if (form.foreigner_type === "Student") {
                if (!form.institute_name) return "Institute name is required";
                if (!form.institute_address_1) return "Institute address is required";
                if (!form.institute_city) return "Institute city is required";
                if (!form.institute_postal_code) return "Institute postcode is required";
                if (!form.institute_state) return "Institute state is required";
            } else if (form.foreigner_type === "Worker") {
                if (!form.employer_name) return "Employer name is required";
                if (!form.employer_address_1) return "Employer address is required";
                if (!form.employer_city) return "Employer city is required";
                if (!form.employer_postal_code) return "Employer postcode is required";
                if (!form.employer_state) return "Employer state is required";
            } else if (form.foreigner_type === "Tourist") {
                if (!form.hotel_name) return "Hotel name is required";
            }
        }

        if (isPassport && !form.identity_expiry_date) return "Identity Expiry Date is required";
        if (showApprovalFields) {
            if (!approvalForm.current_address.trim()) return "Current Address is required";
            // Document Info Type is required for passport, optional otherwise.
            if (isPassport && !approvalForm.identity_doc_type) return "Document Info Type is required";
            if (approvalForm.identity_doc_type) {
                // Name is required for every document info type.
                if (!approvalForm.identity_name_info.trim()) return "Name Info is required";
                if (requiresAddressInfo && !approvalForm.identity_address_info.trim()) return "Address Info is required";
                if (isSupervisorDoc) {
                    if (!approvalForm.identity_phone_info.trim()) return "Phone Number is required";
                    if (!approvalForm.identity_info_no.trim()) return "Identity Number is required";
                    if (!approvalForm.identity_relationship_info.trim()) return "Relationship is required";
                }
            }
        }

        return null; // No errors
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!privacyAccepted) return;
        setLoading(true);

        const validationError = validateForm();
        if (validationError) {
            setErrorModal({ open: true, message: validationError });
            setLoading(false);
            // Find and focus the first invalid field
            const fieldName = validationError.split(" ")[0].toLowerCase();
            const inputElement = document.querySelector(`[name="${fieldName}"]`);
            if (inputElement) {
                inputElement.focus();
            }
            return;
        }

        try {
            const hasDocType = !!approvalForm.identity_doc_type;
            const body = {
                msisdn: form.msisdn,
                full_name: form.full_name,
                first_name: form.first_name,
                gender: form.gender,
                date_of_birth: form.date_of_birth,
                identity_expiry_date: isPassport ? form.identity_expiry_date : null,
                current_address: approvalForm.current_address,
                identity_doc_type: hasDocType ? approvalForm.identity_doc_type : null,
                identity_name_info: hasDocType ? (approvalForm.identity_name_info.trim() || null) : null,
                identity_address_info: hasDocType ? (approvalForm.identity_address_info.trim() || null) : null,
                identity_phone_info: hasDocType ? (approvalForm.identity_phone_info.trim() || null) : null,
                identity_info_no: hasDocType ? (approvalForm.identity_info_no.trim() || null) : null,
                identity_relationship_info: hasDocType ? (approvalForm.identity_relationship_info.trim() || null) : null,
                address1: form.address1,
                address2: form.address2,
                state: form.state,
                city: form.city,
                postal_code: form.postal_code,
                email: form.email,
                plan_info: "JS", // Adjust if needed
                identity_type: form.identity_type === 'MYKAD' ? 'NRIC' : form.identity_type,
                identity_no: form.identity_no,
                iccid: form.iccid,
                issuance_country: form.issuance_country,
                alt_mobile_no: form.alt_mobile_no,
                stay_address_1: form.stay_address_1,
                stay_address_2: form.stay_address_2,
                stay_city: form.stay_city,
                stay_state: form.stay_state,
                stay_postal_code: form.stay_postal_code,
                ugu_ref_code: form.ugu_ref_code,
            };
            Object.assign(body, dealerSdkPayload(sdkResponse));

            if (isPassport) {
                body.foreigner_type = form.foreigner_type;

                switch (form.foreigner_type) {
                    case 'Student':
                        Object.assign(body, {
                            institute_name: form.institute_name,
                            institute_address_1: form.institute_address_1,
                            institute_address_2: form.institute_address_2,
                            institute_city: form.institute_city,
                            institute_state: form.institute_state,
                            institute_postal_code: form.institute_postal_code,
                            institute_ref_no: form.institute_ref_no,
                        });
                        break;
                    case 'Worker':
                        Object.assign(body, {
                            employer_name: form.employer_name,
                            employer_address_1: form.employer_address_1,
                            employer_address_2: form.employer_address_2,
                            employer_city: form.employer_city,
                            employer_state: form.employer_state,
                            employer_postal_code: form.employer_postal_code,
                            emp_ref_no: form.emp_ref_no,
                        });
                        break;
                    case 'Tourist':
                        Object.assign(body, {
                            hotel_name: form.hotel_name,
                        });
                        break;
                    default:
                        break;
                }
            }

            await submitPayload(body);
        } finally {
            setLoading(false);
        }
    };
    // Submit payload to the appropriate API endpoint

    const submitPayload = async (payload) => {
        if (!payload || submitting.current) return;
        submitting.current = true;
        setLoading(true);
        try {
            const apiEndpoint = useV1Api ? 'api/v1/register-customer' : 'api/v2/register-customer';
            const response = await axiosInstance.post(apiEndpoint, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                navigate("/dealer-register-success");
                // toast.success(response.data.message || "Customer registered successfully!");
                // setTimeout(() => {
                //     navigate("/home");
                // }, 2000);
                // Optionally reset form here
                // setForm(initialFormState);

            }
        } catch (error) {
            setErrorModal({ open: true, message: error?.response?.data?.message || "Registration failed. Please try again." });
        } finally {
            submitting.current = false;
            setLoading(false);
        }
    };

    const handleOpenBarCodeModal = () => {
        setOpenModal(true);
    };

    const closeModal = () => {
        setOpenModal(false);
    };

    const getCode = (code) => {
        if (code) {
            setForm(prev => ({
                ...prev,
                iccid: code,
            }));
        }
    };

    const filteredAvailableNos = availableNos.filter((number) =>
        String(number.MSISDN).includes(form.msisdn.trim())
    );

    return (
        <>
            <Header />
            <div className="max-w-md my-6 px-4 mx-auto bg-white rounded-lg ">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Full Name */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Full Name <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="full_name"
                            disabled={isLocked("full_name")}
                            value={form.full_name}
                            onChange={handleChange}
                            className="mt-1 block w-full min-h-[50px] text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* First Name */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">First Name <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="first_name"
                            value={form.first_name}
                            onChange={handleChange}
                            className="mt-1 block w-full min-h-[50px] text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Gender <span className="text-red-500">*</span></label>
                        <select
                            autoComplete="off"
                            name="gender"
                            disabled={isLocked("gender")}
                            value={form.gender}
                            onChange={handleChange}
                            className="font-poppins w-full p-3  outline-none text-[#1111117A] rounded-[8px] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            className="mt-1 block w-full min-h-[50px] text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Date of Birth */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Date of Birth (as on Identity) <span className="text-red-500">*</span></label>

                        <div className="mt-1 box-border flex h-[50px] w-full min-w-0 items-center rounded-[8px] border border-[#11111126] px-3 focus-within:border-blue-500">
                            <Input
                                autoComplete="off"
                                type={"date"}
                                name={"date_of_birth"}
                                isDisabled={isLocked("date_of_birth")}
                                intent={"secondary"}
                                size={"lg"}
                                placeholder={"dob"}
                                roundness={"round-sm"}
                                classes={"!p-0 !border-0 box-border h-full w-full min-w-0 max-w-full text-[#1111117A]"}
                                value={form?.date_of_birth}
                                selector={"dob"}
                                eventAction={handleChange}
                            />
                        </div>
                    </div>

                    {/* Identity Type */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Identity Type <span className="text-red-500">*</span></label>
                        <select
                            autoComplete="off"
                            name="identity_type"
                            disabled={isLocked("identity_type")}
                            value={form.identity_type}
                            onChange={handleChange}
                            className="font-poppins w-full p-3  outline-none text-[#1111117A] rounded-[8px] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                        >
                            <option value="">Select Identity Type</option>
                            <option value="MYKAD">MYKAD</option>
                            <option value="PASSPORT">Passport</option>
                        </select>
                    </div>

                    {/* Identity Number */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Identity Number <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="identity_no"
                            disabled={isLocked("identity_no")}
                            value={form.identity_no}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Additional identity and approval fields */}
                    {showApprovalFields && (
                        <>
                            

                            {/* Identity Expiry Date */}
                            {isPassport && (
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Identity Expiry Date <span className="text-red-500">*</span></label>
                                <div className="mt-1 box-border flex h-[50px] w-full min-w-0 items-center rounded-[8px] border border-[#11111126] px-3 focus-within:border-blue-500">
                                    <Input
                                        autoComplete="off"
                                        type={"date"}
                                        name={"identity_expiry_date"}
                                        isDisabled={isLocked("identity_expiry_date")}
                                        intent={"secondary"}
                                        size={"lg"}
                                        placeholder={"expiry date"}
                                        roundness={"round-sm"}
                                        classes={"!p-0 !border-0 box-border h-full w-full min-w-0 max-w-full text-[#1111117A]"}
                                        value={form.identity_expiry_date}
                                        selector={"identity_expiry_date"}
                                        eventAction={handleChange}
                                    />
                                </div>
                            </div>
                            )}

                            {/* Current Address */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Current Address <span className="text-red-500">*</span></label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="current_address"
                                    value={approvalForm.current_address}
                                    onChange={handleApprovalChange}
                                    className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Document Info Type */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">
                                    Document Info Type {isPassport && <span className="text-red-500">*</span>}
                                </label>
                                <select
                                    autoComplete="off"
                                    name="identity_doc_type"
                                    value={approvalForm.identity_doc_type}
                                    onChange={handleApprovalChange}
                                    className="font-poppins w-full p-3 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                >
                                    <option value="">Select Document Info Type</option>
                                    {docInfoTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Child form of Document Info Type */}
                            {approvalForm.identity_doc_type && (
                                <div className="mx-4 space-y-4">
                                    {/* Name Info */}
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="identity_name_info"
                                            value={approvalForm.identity_name_info}
                                            onChange={handleApprovalChange}
                                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>

                                    {/* Address Info */}
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Address {requiresAddressInfo && <span className="text-red-500">*</span>}</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="identity_address_info"
                                            value={approvalForm.identity_address_info}
                                            onChange={handleApprovalChange}
                                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>

                                    {/* Phone Info */}
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Phone Number {isSupervisorDoc && <span className="text-red-500">*</span>}</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="identity_phone_info"
                                            value={approvalForm.identity_phone_info}
                                            onChange={handleApprovalChange}
                                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>

                                    {/* Identity Info */}
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Identity Number {isSupervisorDoc && <span className="text-red-500">*</span>}</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="identity_info_no"
                                            value={approvalForm.identity_info_no}
                                            onChange={handleApprovalChange}
                                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>

                                    {/* Relationship Info */}
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Relationship {isSupervisorDoc && <span className="text-red-500">*</span>}</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="identity_relationship_info"
                                            value={approvalForm.identity_relationship_info}
                                            onChange={handleApprovalChange}
                                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Additional fields for passport */}
                    {isPassport && (
                        <>
                            {/* Foreigner Type */}
                            {/* <div>
                                <label className="block text-[13px] font-medium text-gray-700">Foreigner Type <span className="text-red-500">*</span></label>
                                <select
                                    autoComplete="off"
                                    name="foreigner_type"
                                    value={form.foreigner_type}
                                    onChange={handleChange}
                                    className="font-poppins w-full p-3  outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] rounded-[8px] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                >
                                    <option value="">Select Foreigner Type</option>
                                    <option value="Student">Student</option>
                                    <option value="Worker">Worker</option>
                                    <option value="Tourist">Tourist</option>
                                </select>
                            </div> */}

                            {/* Alternate Mobile */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Alternate Mobile</label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="alt_mobile_no"
                                    value={form.alt_mobile_no}
                                    onChange={handleChange}
                                    className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Issuance Country */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Identity Issuing Country <span className="text-red-500">*</span></label>
                                {/* {form.identity_type === "PASSPORT" ? ( */}
                                <select
                                    autoComplete="off"
                                    name="issuance_country"
                                    disabled={isLocked("issuance_country")}
                                    value={form.issuance_country}
                                    onChange={handleChange}
                                    className="font-poppins w-full p-3 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                >
                                    <option value="">Select Issuance Country</option>
                                    {countries.map((country) => (
                                        <option key={country.code} value={country.code}>
                                            {country.name} ({country.code})
                                        </option>
                                    ))}
                                </select>
                                {/* ) : ( */}
                                {/* <input
                                        name="issuance_country"
                                        value="MY"
                                        disabled
                                        className="mt-1 block w-full border-2 border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
                                    />
                                )} */}
                            </div>

                            {/* Staying Address 1 */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying Address Line 1 <span className="text-red-500">*</span></label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_address_1"
                                    value={form.stay_address_1}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Staying Address 2 */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying Address Line 2</label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_address_2"
                                    value={form.stay_address_2}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Staying City */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying City <span className="text-red-500">*</span></label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_city"
                                    value={form.stay_city}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Staying State */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying State <span className="text-red-500">*</span></label>
                                <select
                                    autoComplete="off"
                                    name="stay_state"
                                    value={form.stay_state}
                                    onChange={handleChange}
                                    className="font-poppins w-full p-3 rounded-[8px] outline-none text-base bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                >
                                    <option value="">Select State</option>
                                    {states.map((state) => (
                                        <option key={state.code} value={state.code}>
                                            {state.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Staying Postcode */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying Postcode <span className="text-red-500">*</span></label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_postal_code"
                                    value={form.stay_postal_code}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Referral Code</label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="ugu_ref_code"
                                    value={form.ugu_ref_code}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Additional fields based on foreigner type */}
                            {form.foreigner_type === "Student" && (
                                <>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Name <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_name"
                                            value={form.institute_name}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Address Line 1 <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_address_1"
                                            value={form.institute_address_1}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Address Line 2</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_address_2"
                                            value={form.institute_address_2}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute City <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_city"
                                            value={form.institute_city}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Postcode <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_postal_code"
                                            value={form.institute_postal_code}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute State <span className="text-red-500">*</span></label>
                                        <select
                                            autoComplete="off"
                                            name="institute_state"
                                            value={form.institute_state}
                                            onChange={handleChange}
                                            className="font-poppins w-full p-3 outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] rounded-[8px] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                        >
                                            <option value="">Select State</option>
                                            {states.map((state) => (
                                                <option key={state.code} value={state.code}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Reference No</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_ref_no"
                                            value={form.institute_ref_no}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </>
                            )}

                            {form.foreigner_type === "Worker" && (
                                <>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Name <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_name"
                                            value={form.employer_name}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Address Line 1 <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_address_1"
                                            value={form.employer_address_1}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Address Line 2</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_address_2"
                                            value={form.employer_address_2}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer City <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_city"
                                            value={form.employer_city}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Postcode <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_postal_code"
                                            value={form.employer_postal_code}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer State <span className="text-red-500">*</span></label>
                                        <select
                                            autoComplete="off"
                                            name="employer_state"
                                            value={form.employer_state}
                                            onChange={handleChange}
                                            className="font-poppins w-full p-3 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                        >
                                            <option value="">Select State</option>
                                            {states.map((state) => (
                                                <option key={state.code} value={state.code}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employee Reference No</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="emp_ref_no"
                                            value={form.emp_ref_no}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </>
                            )}

                            {form.foreigner_type === "Tourist" && (
                                <div>
                                    <label className="block text-[13px] font-medium text-gray-700">Hotel Name <span className="text-red-500">*</span></label>
                                    <input
                                        autoComplete="off"
                                        type="text"
                                        name="hotel_name"
                                        value={form.hotel_name}
                                        onChange={handleChange}
                                        className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            )}

                            <div className="flex items-center mt-2">
                                <input
                                    autoComplete="off"
                                    type="checkbox"
                                    checked={sameAddress}
                                    onChange={(e) => setSameAddress(e.target.checked)}
                                    id="sameAddress"
                                    className="h-4 min-h-[50px] w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="sameAddress" className="ml-2 text-[13px] font-medium text-gray-700">
                                    Address is same as staying address
                                </label>
                            </div>
                        </>
                    )}

                    {/* Address 1 */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Identity Address <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="address1"
                            // disabled={isLocked("address1")}
                            value={form.address1}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Address 2 */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Address Line 2</label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="address2"
                            // disabled={isLocked("address2")}
                            value={form.address2}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* City */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">City <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="city"
                            // disabled={isLocked("city")}
                            value={form.city}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* State - Now a dropdown */}
                    <div>
                        <label className="block  text-[13px] font-medium text-gray-700">State <span className="text-red-500">*</span></label>
                        <select
                            autoComplete="off"
                            name="state"
                            // disabled={isLocked("state")}
                            value={form.state}
                            onChange={handleChange}
                            className="font-poppins w-full p-3 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                        >
                            <option value="">Select State</option>
                            {states.map((state) => (
                                <option key={state.code} value={state.code}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Postcode */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Postcode <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="postal_code"
                            // disabled={isLocked("postal_code")}
                            value={form.postal_code}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* ICCID with barcode scanner */}
                    <div className="relative">
                        <label className="block text-[13px] font-medium text-gray-700">ICCID <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="iccid"
                            value={form.iccid}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="absolute top-9 right-3">
                            <CiBarcode
                                className="size-6 cursor-pointer text-gray-500 hover:text-gray-700"
                                onClick={handleOpenBarCodeModal}
                            />
                        </div>
                    </div>

                    {/* MSISDN */}
                    <div className="space-y-1">
                        <label className="block text-[13px] font-medium text-gray-700">MSISDN <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input
                                autoComplete="off"
                                name="msisdn"
                                value={form.msisdn}
                                onChange={handleChange}
                                onFocus={() => setIsMsisdnDropdownOpen(true)}
                                onBlur={() => setTimeout(() => setIsMsisdnDropdownOpen(false), 150)}
                                placeholder="Select Mobile Number"
                                className="font-poppins w-full p-3 pr-12 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                            />
                            <button
                                type="button"
                                onClick={() => getAvailableNos(true)}
                                disabled={numbersLoading}
                                title="Refresh numbers"
                                aria-label="Refresh available numbers"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <FiRefreshCw className={`size-5 ${numbersLoading ? "animate-spin" : ""}`} />
                            </button>
                            {isMsisdnDropdownOpen && (
                                <div className="absolute z-20  max-h-48 w-full overflow-y-auto rounded-[8px] border-[1px] border-[#11111126] bg-white shadow-sm">
                                    {filteredAvailableNos.length > 0 ? (
                                        filteredAvailableNos.map((number) => (
                                            <button
                                                type="button"
                                                key={number.MSISDN}
                                                onMouseDown={() => handleMsisdnSelect(number.MSISDN)}
                                                className="font-poppins block w-full px-3 py-2 text-left  text-[#1111117A] hover:bg-gray-50"
                                            >
                                                {number.MSISDN}
                                            </button>
                                        ))
                                    ) : (
                                        <p className="font-poppins px-3 py-2  text-[#1111117A]">
                                            No mobile number found
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* API Version Selection */}
                    {/* <div className="flex items-center mt-4">
                        <input
                            autoComplete="off"
                            type="checkbox"
                            checked={useV1Api}
                            onChange={(e) => setUseV1Api(e.target.checked)}
                            id="useV1Api"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="useV1Api" className="ml-2 text-[13px] font-medium text-gray-700">
                            Registration VIA P2
                        </label>
                    </div> */}

                    {/* Privacy Notice consent: Submit stays disabled until accepted */}
                    <PrivacyConsent checked={privacyAccepted} onChange={setPrivacyAccepted} />

                    {/* Submit Button */}
                    <div>
                        <button
                            type="submit"
                            disabled={loading || !privacyAccepted}
                            className="mt-2 w-full bg-primary text-white py-4 rounded-full text-[12px] font-[400] transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loading ? "Processing..." : "Submit"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Error Modal */}
            {errorModal.open && (
                <ErrorModal
                    message={errorModal.message}
                    close={() => setErrorModal({ open: false, message: "" })}
                />
            )}

            {/* Scanner Modal */}
            {openModal && <ScannerModal closeModal={closeModal} getCode={getCode} />}
        </>
    );
};

export default DealerRegister;
